// src/components/admin/UpdateRecipe.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Alert,
  Box,
  TextField,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Button,
  Grid,
  IconButton,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Autocomplete from "@mui/material/Autocomplete";
import { AddCircle, RemoveCircle } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import { useTranslation } from "react-i18next";
import BackToDashboard from "../common/BackToAdminDashboard.js";
import defaultRecipeImg from "../../assets/default-recipe.png";

const LANGS = [
  "el", "en", "ar", "bg", "de", "en_gb", "en_us", "es", "fr", "hr", "it", "ro", "ru", "sq", "sr", "sv", "ur", "zh"
];
const initLangObj = () => Object.fromEntries(LANGS.map((l) => [l, ""]));

function toApiLang(lang) {
  if (!lang) return "en";
  if (lang === "el") return "gr";
  return lang;
}

const normalizeTrLangKey = (k) => (k === "gr" ? "el" : k);

const normalizeTranslationsDict = (obj) => {
  const out = {};
  if (!obj || typeof obj !== "object") return out;
  for (const [k, v] of Object.entries(obj)) {
    out[normalizeTrLangKey(k)] = v ?? "";
  }
  return out;
};

const toNumberArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.map((x) => Number(x)).filter((x) => !Number.isNaN(x));
  if (typeof val === "string") {
    return val
      .split(",")
      .map((x) => Number(x.trim()))
      .filter((x) => !Number.isNaN(x));
  }
  return [];
};

const trimTrailingSlash = (value) => String(value || "").replace(/\/+$/, "");
const frontendBaseUrl = trimTrailingSlash(
  process.env.REACT_APP_FRONTEND_URL ||
    (typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "http://localhost:3000")
);
const uploadBaseUrl = trimTrailingSlash(
  process.env.REACT_APP_UPLOAD_API_BASE || frontendBaseUrl
);

async function uploadAndSaveRecipeImage(recipeId, file) {
  const fd = new FormData();
  fd.append("image", file);

  const up = await fetch(`${uploadBaseUrl}/upload-api/recipe-image.php?debug=1`, {
    method: "POST",
    body: fd,
    redirect: "manual",
    credentials: "omit", // ✅ δεν χρειάζονται cookies για upload στο Plesk
  });

  // ✅ πολύ σημαντικό: δες τι γυρνάει πριν κάνεις json()
  const text = await up.text();
  let upJson = null;
  try { upJson = JSON.parse(text); } catch (e) { }

  if (!up.ok || !upJson?.success) {
    console.error("PLESK UPLOAD RAW:", text);
    throw new Error(upJson?.error || `Upload to Plesk failed (HTTP ${up.status})`);
  }

  const imageUrl = upJson.image_url; // full url

  const res2 = await api.recipes.setImageUrl(recipeId, imageUrl);
  if (!res2?.data?.success) {
    throw new Error(res2?.data?.error || "DB update failed");
  }

  return imageUrl;
}

export default function UpdateRecipe() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  // Which recipe
  const [selectedRecipeId, setSelectedRecipeId] = useState(id || "");
  const [recipeList, setRecipeList] = useState([]);

  // Translations state
  const [names, setNames] = useState(initLangObj);
  const [ingredientsByLang, setIngredientsByLang] = useState(initLangObj);
  const [instructionsByLang, setInstructionsByLang] = useState(initLangObj);

  // Relationships
  const [mealTypeIds, setMealTypeIds] = useState([]);
  const [foodTypeIds, setFoodTypeIds] = useState([]);
  const [countryRecipesId, setCountryRecipesId] = useState("");
  const [dietTypeIds, setDietTypeIds] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [religiousPreferenceIds, setReligiousPreferenceIds] = useState([]);

  // Reference data
  const [mealTypes, setMealTypes] = useState([]);
  const [foodTypes, setFoodTypes] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [dietTypes, setDietTypes] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [religiousPrefsList, setReligiousPrefsList] = useState([]);
  const [genericProductList, setGenericProductList] = useState([]);

  // Other fields
  const [difficulty, setDifficulty] = useState("");
  const [prepTime, setPrepTime] = useState("");
  const [calories, setCalories] = useState("");
  const [visibility, setVisibility] = useState("private");

  // Image
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState("");

  // Ingredients array (generic products)
  const [products, setProducts] = useState([{ generic_product_id: "", quantity: "" }]);

  // UI
  const [refDataLoading, setRefDataLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const requiredLangs = useMemo(() => (["el", "en"]), []);

  const frontenddUrl = uploadBaseUrl;

  const getFullImageUrl = (url) => {
    if (!url) return defaultRecipeImg;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/")) return frontenddUrl + url; // ✅ πιάνει /uploads/... και /static/...
    return frontenddUrl + "/uploads/recipes/" + url.replace(/^\/+/, "");
  };


  const getGpLabel = (gp) => {
    const lang = i18n.language === "gr" ? "el" : i18n.language;
    const key = lang === "el" ? "name_el" : `name_${lang}`;

    return (
      gp?.[key] ||
      gp?.name ||        // ✅ ΚΡΙΣΙΜΟ: αν backend δίνει localized "name"
      gp?.name_gr ||
      gp?.name_el ||
      gp?.name_en ||
      ""
    ).toString();
  };


  // Fetch reference data + recipe list
  useEffect(() => {
    let mounted = true;

    const fetchReferenceData = async () => {
      try {
        setRefDataLoading(true);
        setError("");

        const [
          mealTypesRes,
          foodTypesRes,
          countryRes,
          dietRes,
          seasonsRes,
          religiousRes,
          recipeListRes,
          genericProdsRes,
        ] = await Promise.all([
          api.household.getMealTypes(),
          api.household.getFoodTypes(),
          api.household.getCountryRecipes(),
          api.household.getDietTypes(),
          api.household.getSeasons(),
          api.household.getReligiousPreferences(),
          api.recipes.myList(toApiLang(i18n.language)),
          api.genericProducts.list(),
        ]);

        if (!mounted) return;

        setMealTypes(Array.isArray(mealTypesRes?.data) ? mealTypesRes.data : []);
        setFoodTypes(Array.isArray(foodTypesRes?.data) ? foodTypesRes.data : []);
        setCountryList(Array.isArray(countryRes?.data) ? countryRes.data : []);
        setDietTypes(Array.isArray(dietRes?.data) ? dietRes.data : []);
        setSeasons(Array.isArray(seasonsRes?.data) ? seasonsRes.data : []);
        const relPayload = religiousRes?.data;
        const relItems = Array.isArray(relPayload?.items) ? relPayload.items : [];
        setReligiousPrefsList(relItems);

        console.log("religiousRes.data =", religiousRes?.data);

        const listData =
          Array.isArray(recipeListRes?.data?.data) ? recipeListRes.data.data :
            Array.isArray(recipeListRes?.data) ? recipeListRes.data :
              [];
        setRecipeList(listData);

        const gpData =
          Array.isArray(genericProdsRes?.data) ? genericProdsRes.data :
            Array.isArray(genericProdsRes?.data?.data) ? genericProdsRes.data.data :
              [];
        setGenericProductList(gpData);
      } catch (err) {
        console.error(err);
        setError(t("failed_to_load_reference_data") || "Failed to load reference data.");
      } finally {
        setRefDataLoading(false);
      }
    };

    fetchReferenceData();
    return () => { mounted = false; };
  }, [t, i18n.language]);

  // Fetch recipe data when selectedRecipeId changes
  useEffect(() => {

    if (!selectedRecipeId || String(selectedRecipeId) === "0") return;

    let mounted = true;

    const fetchRecipe = async () => {
      try {
        setLoading(true);
        setError("");
        setSuccess("");

        const res = await api.recipes.getOne(selectedRecipeId, { params: { lang: toApiLang(i18n.language) } });
        if (!mounted) return;

        if (!res?.data?.success) {
          setError(res?.data?.error || t("failed_to_load_recipe_data") || "Failed to load recipe data.");
          return;
        }

        const recipe = res.data.data || {};
        const tr = recipe.translations || {};

        const trName = normalizeTranslationsDict(tr.name);
        const trIng = normalizeTranslationsDict(tr.ingredients);
        const trIns = normalizeTranslationsDict(tr.instructions);

        // fallback που κοιτάει και *_gr αν υπάρχει
        const fallbackNames = {};
        const fallbackIngredients = {};
        const fallbackInstructions = {};

        for (const lng of LANGS) {
          const dbLng = lng === "el" ? "gr" : lng;

          fallbackNames[lng] =
            (recipe[`name_${lng}`] ?? recipe[`name_${dbLng}`] ?? "").toString();

          fallbackIngredients[lng] =
            (recipe[`ingredients_${lng}`] ?? recipe[`ingredients_${dbLng}`] ?? "").toString();

          fallbackInstructions[lng] =
            (recipe[`instructions_${lng}`] ?? recipe[`instructions_${dbLng}`] ?? "").toString();
        }

        setNames((prev) => ({ ...prev, ...fallbackNames, ...trName }));
        setIngredientsByLang((prev) => ({ ...prev, ...fallbackIngredients, ...trIng }));
        setInstructionsByLang((prev) => ({ ...prev, ...fallbackInstructions, ...trIns }));


        // ids
        setMealTypeIds(Array.isArray(recipe.meal_type_ids) ? recipe.meal_type_ids : []);
        setFoodTypeIds(Array.isArray(recipe.food_type_ids) ? recipe.food_type_ids : []);
        setCountryRecipesId(recipe.country_recipes_id || "");
        setDietTypeIds(Array.isArray(recipe.diet_type_ids) ? recipe.diet_type_ids.map(String) : []);
        setSeasonId(recipe.season_id || "");
        setReligiousPreferenceIds(toNumberArray(recipe.religious_preference_ids));
        console.log("religiousPrefsList", religiousPrefsList.length, religiousPrefsList.slice(0, 3));
        console.log("religiousPreferenceIds", religiousPreferenceIds);


        // other fields
        setDifficulty(recipe.difficulty || "");
        setPrepTime(recipe.prep_time != null ? String(recipe.prep_time) : "");
        setCalories(recipe.calories != null ? String(recipe.calories) : "");
        setImageUrl(recipe.image_url || "");
        setVisibility(recipe.visibility || "private");

        // products
        if (Array.isArray(recipe.products) && recipe.products.length > 0) {
          setProducts(
            recipe.products.map((p) => ({
              generic_product_id: p.generic_product_id ? String(p.generic_product_id) : "",
              quantity: p.quantity != null ? String(p.quantity) : "",
            }))
          );
        } else {
          setProducts([{ generic_product_id: "", quantity: "" }]);
        }
      } catch (err) {
        console.error(err);
        setError(t("failed_to_load_recipe_data") || "An error occurred while fetching the recipe data.");
      } finally {
        setLoading(false);
      }
    };

    fetchRecipe();
    return () => { mounted = false; };
  }, [selectedRecipeId, t, i18n.language]);

  const handleRecipeSelection = (e) => {
    const newId = e.target.value;
    setSelectedRecipeId(newId);
    if (newId) navigate(`/admin/update-recipe/${newId}`);
  };

  const addProd = () => setProducts((prev) => [...prev, { generic_product_id: "", quantity: "" }]);
  const removeProd = (index) => setProducts((prev) => prev.filter((_, i) => i !== index));
  const handleProductChange = (index, field, value) => {
    setProducts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const buildTranslationsPayload = () => {
    const out = {};
    for (const lng of LANGS) out[`name_${lng}`] = (names[lng] || "").trim();
    for (const lng of LANGS) out[`ingredients_${lng}`] = (ingredientsByLang[lng] || "").trim();
    for (const lng of LANGS) out[`instructions_${lng}`] = (instructionsByLang[lng] || "").trim();

    if (!out.name_el && out.name_en) out.name_el = out.name_en;
    if (!out.name_en && out.name_el) out.name_en = out.name_el;

    return out;
  };

  const validateRequired = () => {
    const nameEl = (names.el || "").trim();
    const nameEn = (names.en || "").trim();
    if (!nameEl && !nameEn) {
      setError("Πρέπει να υπάρχει τουλάχιστον name_el ή name_en.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!selectedRecipeId) {
      setError("Διάλεξε συνταγή πρώτα.");
      return;
    }
    if (!validateRequired()) return;

    setLoading(true);

    try {
      // =========================
      // 1) UPDATE RECIPE (χωρίς image upload)
      // =========================
      const payload = {
        ...buildTranslationsPayload(),

        meal_type_ids: mealTypeIds.map((x) => parseInt(x, 10)),
        food_type_ids: foodTypeIds.map((x) => parseInt(x, 10)),
        country_recipes_id: countryRecipesId ? parseInt(countryRecipesId, 10) : null,
        diet_type_ids: dietTypeIds.map((x) => parseInt(x, 10)),
        season_id: seasonId ? parseInt(seasonId, 10) : null,
        religious_preference_ids: religiousPreferenceIds.map((x) => parseInt(x, 10)),

        difficulty: difficulty || null,
        prep_time: prepTime ? parseInt(prepTime, 10) : null,
        calories: calories ? parseInt(calories, 10) : null,

        image_url: imageUrl || "",
        visibility,

        products: (products || []).map((p) => ({
          generic_product_id: p.generic_product_id ? parseInt(p.generic_product_id, 10) : null,
          quantity: p.quantity ? parseFloat(p.quantity) : 0,
        })),
      };

      console.log("[1] updating recipe payload...");
      const res = await api.recipes.update(selectedRecipeId, payload);
      console.log("[1] update response:", res?.data);

      if (!res?.data?.success) {
        setError(res?.data?.error || t("failed_to_update_recipe") || "Failed to update recipe.");
        setLoading(false);
        return;
      }

      // =========================
      // 2) IF NEW IMAGE -> UPLOAD TO BACKEND (same server)
      // =========================
      if (imageFile) {
        const newUrl = await uploadAndSaveRecipeImage(selectedRecipeId, imageFile);
        setImageUrl(newUrl);
        setImageFile(null);
      }

      setSuccess(t("recipe_updated_successfully") || "Recipe updated successfully!");
    } catch (err) {
      console.error("UPDATE ERROR:", err);
      console.error("Server says:", err?.response?.data);
      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        t("an_error_occurred_while_updating_recipe") ||
        "Error updating recipe."
      );
    } finally {
      setLoading(false);
    }
  };


  if (refDataLoading) {
    return (
      <Container sx={{ mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <BackToDashboard dashboardPath="/admin/dashboard" />
      <Typography variant="h4" gutterBottom>
        {t("update_recipe_admin") || "Update Recipe (Admin)"}
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>{t("select_recipe") || "Select Recipe"}</InputLabel>
        <Select
          value={selectedRecipeId}
          onChange={handleRecipeSelection}
          label={t("select_recipe") || "Select Recipe"}
        >
          <MenuItem value="">
            <em>{t("no_recipe_selected") || "No recipe selected"}</em>
          </MenuItem>
          {recipeList.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              {r.name || `Recipe #${r.id}`} (ID: {r.id})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CircularProgress size={24} sx={{ mr: 2 }} />
          <Typography>{t("loading") || "Loading..."}</Typography>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          {/* ===== Translations ===== */}
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Μεταφράσεις (όλα τα πεδία)
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {requiredLangs.map((lng) => (
            <Box key={lng} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {lng.toUpperCase()} (required)
              </Typography>

              <TextField
                label={`name_${lng}`}
                fullWidth
                required
                value={names[lng] || ""}
                onChange={(e) => setNames((p) => ({ ...p, [lng]: e.target.value }))}
                sx={{ mb: 1 }}
              />

              <TextField
                label={`ingredients_${lng}`}
                fullWidth
                multiline
                minRows={3}
                value={ingredientsByLang[lng] || ""}
                onChange={(e) => setIngredientsByLang((p) => ({ ...p, [lng]: e.target.value }))}
                sx={{ mb: 1 }}
              />

              <TextField
                label={`instructions_${lng}`}
                fullWidth
                multiline
                minRows={3}
                value={instructionsByLang[lng] || ""}
                onChange={(e) => setInstructionsByLang((p) => ({ ...p, [lng]: e.target.value }))}
              />
            </Box>
          ))}

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Υπόλοιπες γλώσσες (optional)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {LANGS.filter((l) => !requiredLangs.includes(l)).map((lng) => (
                <Box key={lng} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {lng.toUpperCase()}
                  </Typography>

                  <TextField
                    label={`name_${lng}`}
                    fullWidth
                    value={names[lng] || ""}
                    onChange={(e) => setNames((p) => ({ ...p, [lng]: e.target.value }))}
                    sx={{ mb: 1 }}
                  />

                  <TextField
                    label={`ingredients_${lng}`}
                    fullWidth
                    multiline
                    minRows={3}
                    value={ingredientsByLang[lng] || ""}
                    onChange={(e) => setIngredientsByLang((p) => ({ ...p, [lng]: e.target.value }))}
                    sx={{ mb: 1 }}
                  />

                  <TextField
                    label={`instructions_${lng}`}
                    fullWidth
                    multiline
                    minRows={3}
                    value={instructionsByLang[lng] || ""}
                    onChange={(e) => setInstructionsByLang((p) => ({ ...p, [lng]: e.target.value }))}
                  />

                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>

          {/* ===== Relationships ===== */}
          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>{t("meal_type") || "Meal Type"}</InputLabel>
            <Select
              multiple
              value={mealTypeIds}
              onChange={(e) => setMealTypeIds(e.target.value)}
              label={t("meal_type") || "Meal Type"}
              renderValue={(selected) =>
                selected
                  .map((id) => mealTypes.find((m) => m.id === id)?.name || "")
                  .filter(Boolean)
                  .join(", ")
              }
            >
              {mealTypes.map((mt) => (
                <MenuItem key={mt.id} value={mt.id}>
                  {mt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>{t("food_type") || "Food Type"}</InputLabel>
            <Select
              multiple
              value={foodTypeIds}
              onChange={(e) => setFoodTypeIds(e.target.value)}
              label={t("food_type") || "Food Type"}
              renderValue={(selected) =>
                selected
                  .map((id) => foodTypes.find((f) => f.id === id)?.type_name || "")
                  .filter(Boolean)
                  .join(", ")
              }
            >
              {foodTypes.map((ft) => (
                <MenuItem key={ft.id} value={ft.id}>
                  {ft.type_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>{t("country_label") || "Country"}</InputLabel>
            <Select
              value={countryRecipesId}
              label={t("country_label") || "Country"}
              onChange={(e) => setCountryRecipesId(e.target.value)}
            >
              <MenuItem value="">
                <em>{t("select_a_country") || "Select a country"}</em>
              </MenuItem>
              {countryList.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.country_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>{t("diet_type_label") || "Diet Type"}</InputLabel>
            <Select
              multiple
              value={dietTypeIds}
              onChange={(e) => setDietTypeIds(e.target.value)}
              label={t("diet_type_label") || "Diet Type"}
              renderValue={(selected) =>
                selected
                  .map((id) => dietTypes.find((d) => d.id.toString() === id?.toString())?.diet_name || "")
                  .filter(Boolean)
                  .join(", ")
              }
            >
              {dietTypes.map((dt) => (
                <MenuItem key={dt.id} value={dt.id.toString()}>
                  {dt.diet_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>{t("season_label") || "Season"}</InputLabel>
            <Select
              value={seasonId}
              label={t("season_label") || "Season"}
              onChange={(e) => setSeasonId(e.target.value)}
            >
              <MenuItem value="">
                <em>{t("select_season") || "Select Season"}</em>
              </MenuItem>
              {seasons.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>{t("religious_preferences_label") || "Religious Preferences"}</InputLabel>

            <Select
              multiple
              value={Array.isArray(religiousPreferenceIds) ? religiousPreferenceIds : []}
              onChange={(e) => setReligiousPreferenceIds(toNumberArray(e.target.value))}
              label={t("religious_preferences_label") || "Religious Preferences"}
              renderValue={(selected) =>
                toNumberArray(selected)
                  .map((id) => religiousPrefsList.find((rp) => Number(rp.id) === Number(id))?.preference_name || "")
                  .filter(Boolean)
                  .join(", ")
              }
            >
              {religiousPrefsList.map((rp) => (
                <MenuItem key={rp.id} value={Number(rp.id)}>
                  {rp.preference_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>


          {/* ===== Other fields ===== */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Difficulty</InputLabel>
            <Select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} label="Difficulty">
              <MenuItem value=""><em>Select Difficulty</em></MenuItem>
              <MenuItem value="easy">Easy</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="hard">Hard</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Preparation Time (minutes)"
            value={prepTime}
            onChange={(e) => setPrepTime(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            type="number"
          />
          <TextField
            label="Calories"
            value={calories}
            onChange={(e) => setCalories(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            type="number"
          />

          {/* Image upload */}
          <Button variant="outlined" component="label" sx={{ mb: 1 }}>
            Upload Image
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
            />
          </Button>

          <Box sx={{ mb: 2 }}>
            {imageFile ? (
              <img
                src={URL.createObjectURL(imageFile)}
                alt="Preview"
                style={{ maxWidth: 220, maxHeight: 220, borderRadius: 8, border: "1px solid #ccc" }}
              />
            ) : (
              imageUrl && (
                <img
                  src={getFullImageUrl(imageUrl)}
                  alt="Recipe"
                  style={{ maxWidth: 220, maxHeight: 220, borderRadius: 8, border: "1px solid #ccc" }}
                />
              )
            )}
          </Box>

          {/* Visibility */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("visibility") || "Visibility"}</InputLabel>
            <Select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              label={t("visibility") || "Visibility"}
            >
              <MenuItem value="private">{t("private") || "Private"}</MenuItem>
              <MenuItem value="public">{t("public") || "Public"}</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>

          {/* ===== Generic Products ===== */}
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            {t("recipe_products") || "Ingredients (Products)"}
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {products.map((p, idx) => (
            <Grid container spacing={2} alignItems="center" key={idx} sx={{ mb: 1 }}>
              <Grid item xs={12} md={7}>
                <Autocomplete
                  options={genericProductList}
                  value={
                    genericProductList.find(
                      (gp) => String(gp.id) === String(p.generic_product_id)
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleProductChange(idx, "generic_product_id", newValue ? String(newValue.id) : "");
                  }}
                  getOptionLabel={(option) => getGpLabel(option)}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("select_product") || "Select product"}
                      placeholder={t("type_to_search") || "Type to search..."}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={8} md={3}>
                <TextField
                  label={t("quantity") || "Quantity"}
                  value={p.quantity}
                  onChange={(e) => handleProductChange(idx, "quantity", e.target.value)}
                  fullWidth
                  type="number"
                  inputProps={{ step: "0.01" }}
                />
              </Grid>

              <Grid item xs={4} md={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <IconButton onClick={() => removeProd(idx)} disabled={products.length <= 1}>
                  <RemoveCircle />
                </IconButton>
                <IconButton onClick={addProd}>
                  <AddCircle />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Divider sx={{ mt: 2, mb: 2 }} />

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {t("save_changes") || "Save Changes"}
            </Button>

            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate("/admin/dashboard")}
            >
              {t("cancel") || "Cancel"}
            </Button>
          </Box>
        </Box>
      )}
    </Container>
  );
}
